

import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import {
  ArrowRight,
  ArrowLeft,
  Linkedin,
  Mail
} from 'lucide-react';

/**
 * Team Page
 * Leadership team and advisory board
 */
export default function TeamPage() {
  usePageMeta(SEO_ROUTES['/company/team']);

  const currentYear = new Date().getFullYear();

  // People, photographs, bios and LinkedIn all taken from the company page on
  // trust.care, which is the source of record for who is on the team. Each
  // photograph is matched to its person by the alt text and LinkedIn URL the
  // site itself pairs them with, not by appearance.
  const leadership = [
    {
      name: 'Vineet Gulati',
      title: 'CEO & Founder',
      photo: '/images/team/vineet-gulati.webp',
      bio: '30+ years in healthcare, 20 in payments. Founded HealthExpense in 2010, the Plaid for commercial health insurance. Grew the Capgemini healthcare practice from $3M to $35M ARR.',
      linkedin: 'https://www.linkedin.com/in/vgulati/',
    },
    {
      name: 'Jesus Saucedo, MD',
      title: 'CMO & Co-Founder',
      photo: '/images/team/jesus-saucedo.jpg',
      bio: 'Former VP, Sutter Health. Emergency department physician leader for over 20 years, with a deep Medi-Cal and health plan network.',
      linkedin: 'https://www.linkedin.com/in/jesussaucedomd/',
    },
    {
      name: 'Abby George',
      title: 'Chief Strategy Officer',
      photo: '/images/team/abby-george.webp',
      bio: 'Former VP, Workplace Investing at Fidelity Investments. Built and scaled its Health Savings Account business from inception to the third largest administrator in the USA. Leads payer strategy.',
      linkedin: 'https://www.linkedin.com/in/abbygeorge01545/',
    },
    {
      name: 'Jen DeLara',
      title: 'VP, Customer Success',
      photo: '/images/team/jen-delara.webp',
      bio: 'Owns health plan and community organization deployments end to end across the California Community Care Hub network.',
      linkedin: 'https://www.linkedin.com/in/jen-delara-777jd/',
    },
  ];

  const medicalAdvisors = [
    {
      name: 'Liza Danzig, MD',
      title: 'Board Member',
      photo: '/images/team/liza-danzig.webp',
      bio: 'Former CMO, PaxVax Vaccines and advisor to HealthExpense. Infectious disease specialist and a nationally recognized leader in vaccine development.',
      linkedin: 'https://www.linkedin.com/in/lisa-danzig-md-3732454/',
    },
    {
      name: 'Aparna Gulati, MD',
      title: 'Assistant Physician in Chief, Quality',
      photo: '/images/team/aparna-gulati.webp',
      bio: 'Physician and Assistant Physician in Chief, Quality GSAA, TPMG (Kaiser Permanente).',
      linkedin: 'https://www.linkedin.com/in/aparna-gulati-md-60a6281a/',
    },
    {
      name: 'Huy V. Ngo, MD',
      title: 'Healthcare Management Workflows',
      photo: '/images/team/huy-ngo.webp',
      bio: 'Physician at Valley Healthcare FQHC. Board-certified geriatric care specialist.',
      linkedin: 'https://www.linkedin.com/in/huy-ngo-b59a0221b/',
    },
  ];

  const advisors = [
    {
      name: 'Jim Hickman',
      role: 'Building and scaling new client relationships',
      photo: '/images/team/jim-hickman.webp',
      bio: 'Former CEO, Center for Youth Wellness and Better Health East Bay (Sutter Health). Advisor, Camden Coalition of Healthcare Providers.',
      linkedin: 'https://www.linkedin.com/in/hickmanjim/',
    },
    {
      name: 'Guadalupe Pacheco',
      role: 'Building and scaling new client relationships',
      photo: '/images/team/guadalupe-pacheco.webp',
      bio: 'Former Senior Health Advisor, U.S. Department of Health and Human Services, Office of Minority Health.',
      linkedin: 'https://www.linkedin.com/in/guadalupe-pacheco-20a2982/',
    },
    {
      name: 'Maria Hernandez',
      role: 'Building and scaling new client relationships',
      photo: '/images/team/maria-hernandez.webp',
      bio: 'Health equity innovator and strategy leader. 50/50 Women on Boards semi-finalist and ESG advocate.',
      linkedin: 'https://www.linkedin.com/in/mariahernandez/',
    },
    {
      name: 'Kevin Mowll',
      role: 'Strategic partnerships',
      photo: '/images/team/kevin-mowll.webp',
      bio: 'Former VP, Tufts Health Plan. Medicare Products, Capital District Physicians Health Plan.',
      linkedin: 'https://www.linkedin.com/in/kevinmowll/',
    },
    {
      name: 'Paul Ford',
      role: 'Strategic solution roadmap development',
      photo: '/images/team/paul-ford.webp',
      bio: 'Managing Partner, DS9. Former executive at Safeway Health, Mercer and Aetna.',
      linkedin: 'https://www.linkedin.com/in/paford26/',
    },
    {
      name: 'Henry Eickelberg',
      role: 'Former board member and advisor',
      photo: '/images/team/henry-eickelberg.webp',
      bio: 'Former Corporate VP, Human Resources, General Dynamics. Presidential appointee and board member, Pension Benefit Guaranty Corporation.',
      linkedin: 'https://www.linkedin.com/in/henryceickelberg/',
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
            Our Team
          </h1>
          <p className="text-xl text-ink-500 max-w-2xl mx-auto">
            Deep healthcare, Medicaid and community-organization operations experience, building one ledger for
            every funder in social care.
          </p>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Leadership Team
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((person) => (
              <div key={person.name} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-8">
                <img
                  src={person.photo}
                  alt={person.name}
                  loading="lazy"
                  className="w-24 h-24 rounded-full mx-auto mb-6 object-cover bg-gray-100 dark:bg-gray-700"
                />
                <div className="text-center">
                  <h3 className="text-xl font-bold text-ink dark:text-white mb-1">
                    {person.name}
                  </h3>
                  <p className="text-v2accent font-medium mb-4">{person.title}</p>
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                    {person.bio}
                  </p>
                  <a
                    href={person.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${person.name} on LinkedIn`}
                    className="inline-flex items-center text-ink dark:text-gray-300 hover:text-v2accent"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Medical & Advisory Board */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Medical Advisory Board
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Leading physicians guiding our clinical approach and quality standards.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {medicalAdvisors.map((advisor) => (
              <div key={advisor.name} className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center shadow-md">
                <img
                  src={advisor.photo}
                  alt={advisor.name}
                  loading="lazy"
                  className="w-20 h-20 rounded-full mx-auto mb-4 object-cover bg-gray-100 dark:bg-gray-700"
                />
                <h3 className="font-bold text-ink dark:text-white mb-1">
                  {advisor.name}
                </h3>
                <p className="text-v2accent text-sm font-medium mb-2">{advisor.title}</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                  {advisor.bio}
                </p>
                <a
                  href={advisor.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${advisor.name} on LinkedIn`}
                  className="inline-flex items-center text-ink dark:text-gray-300 hover:text-v2accent"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>

          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-ink dark:text-white mb-4">
              Strategic Advisors
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advisors.map((advisor) => (
              <div
                key={advisor.name}
                className="bg-white dark:bg-gray-900 rounded-xl p-6 text-center shadow-md"
              >
                <img
                  src={advisor.photo}
                  alt={advisor.name}
                  loading="lazy"
                  className="w-20 h-20 rounded-full mx-auto mb-4 object-cover bg-gray-100 dark:bg-gray-700"
                />
                <h3 className="font-bold text-ink dark:text-white mb-1">{advisor.name}</h3>
                <p className="text-v2accent text-sm font-medium mb-2">{advisor.role}</p>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{advisor.bio}</p>
                <a
                  href={advisor.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${advisor.name} on LinkedIn`}
                  className="inline-flex items-center text-ink dark:text-gray-300 hover:text-v2accent"
                >
                  <Linkedin className="w-4 h-4" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
            Join Our Team
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
            We're building something meaningful. If you're passionate about using technology
            to improve health outcomes for underserved populations, we'd love to hear from you.
          </p>
          <a
            href="mailto:support@weavein.ai"
            className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
          >
            <Mail className="w-5 h-5 mr-2" />
            View Open Positions
          </a>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
